import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

/**
 * ProtectedRoute - A component that wraps a route and checks if the user is authenticated.
 * If the user is authenticated (i.e., there is a valid token), it renders the child components.
 * If the user is not authenticated, it redirects the user to the login page.
 *
 * @component
 * @example
 * <ProtectedRoute>
 *   <SomeComponent />
 * </ProtectedRoute>
 *
 * @param {Object} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered if the user is authenticated.
 * @returns {React.ReactNode} - The child components if the user is authenticated, otherwise a redirect to the login page.
 */
const ProtectedRoute = ({ children }) => {
    // Get the token from AuthContext to determine if the user is authenticated
    const { token } = useContext(AuthContext);

    // If there is a valid token, render the child components (i.e., allow access to the route)
    // Otherwise, redirect the user to the login page
    return token ? children : <Navigate to="/login" replace />;
};

export default ProtectedRoute;
